import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { profileOperations } from '@/lib/db';
import { logger } from '@/lib/logger';
import type { Profile, AuthContextType, SubscriptionData } from '@/types';
import { gtmEvent } from '@/lib/gtm';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      logger.debug('Fetching user profile', { component: 'AuthContext', userId });
      const profile = await profileOperations.getProfile(userId);
      setProfile(profile);
      logger.debug('Profile fetched successfully', { component: 'AuthContext', userId });
    } catch (error) {
      logger.error('Error fetching profile', error, { component: 'AuthContext', userId });
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      logger.debug('Refreshing profile', { component: 'AuthContext', userId: user.id });
      await fetchProfile(user.id);
    }
  }, [user?.id, fetchProfile]);

  const checkSubscription = useCallback(async () => {
    if (!user || !session) {
      setSubscription(null);
      return;
    }

    try {      
      logger.debug('Checking subscription status', { component: 'AuthContext', userId: user.id });
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        logger.error('Error checking subscription', error, { component: 'AuthContext', userId: user.id });
        return;
      }
      
      if (data) {
        logger.debug('Subscription data received', { component: 'AuthContext', subscribed: data.subscribed });
        setSubscription({
          subscribed: data.subscribed || false,
          subscription_tier: data.subscription_tier,
          subscription_status: data.subscription_status,
          current_period_end: data.current_period_end,
          credits_per_period: data.credits_per_period || 0,
          credits_used_this_period: data.credits_used_this_period || 0,
          credits_remaining: data.credits_remaining || 0,
        });
      }
    } catch (error) {
      logger.error('Exception during subscription check', error, { component: 'AuthContext', userId: user?.id });
    }
  }, [user, session]);

  useEffect(() => {
    let mounted = true;
    
    logger.debug('Setting up auth state listener', { component: 'AuthContext' });
    
    // Set up auth state listener
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        logger.debug('Auth state change event', { component: 'AuthContext', event, hasSession: !!session });
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Use setTimeout(0) to defer async operations and prevent auth deadlock
          setTimeout(() => {
            if (mounted) {
              fetchProfile(session.user.id);
            }
          }, 0);
        } else {
          setProfile(null);
          setSubscription(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      
      logger.debug('Initial session check', { component: 'AuthContext', hasSession: !!session });
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(() => {
          if (mounted) {
            fetchProfile(session.user.id);
          }
        }, 0);
      }
      
      setLoading(false);
    });

    return () => {
      mounted = false;
      authSubscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Separate effect for subscription checks to avoid auth deadlock
  useEffect(() => {
    if (user && session && profile) {
      // Only check subscription after profile is loaded
      const timeoutId = setTimeout(() => {
        checkSubscription();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [user, session, profile, checkSubscription]);

  const signUp = async (email: string, password: string, firstName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName || ''
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setSubscription(null);
    gtmEvent('logout', {});
  };

  const value = {
    user,
    session,
    profile,
    subscription,
    loading,
    signUp,
    signIn,
    signOut,
    refreshProfile,
    checkSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};